import React, { useState } from 'react';
import Select from '../ui/Select';
import DateRangeSelector from '../DateRangeSelector';
import { useVisits } from '../../contexts/VisitsContext';
import { obfuscateUsername } from '../../utils/obfuscateUsername';
import { getAdminBadge } from '../../utils/getAdminBadge';
import PopoverMenu from '../ui/PopoverMenu';
import { COMPARISON_MODES, COMPARISON_LABELS, comparisonLabel } from '../../constants/visitsComparison';

import { UNIVERS_ALL } from '../../constants/visitsUnivers';

// Filters shown above the tab content. The project is chosen in the sidebar, so
// this header carries period + univers + salesperson + shop signs.
const VisitsSelectionHeader = ({
  selectedProject,
  loading,
  universOptions = [],
  selectedUnivers,
  setSelectedUnivers,
  salespeople,
  selectedSalespeople,
  setSelectedSalespeople,
  shopSigns,
  selectedShopSigns,
  setSelectedShopSigns,
  obfuscate,
  currentUser,
  showOutOfScope,
  setShowOutOfScope,
  outOfScope = { total: 0, deleted: 0, special: 0, didNotHappen: 0 },
  comparisonMode,
  setComparisonMode,
  viewAsUser = false,
  setViewAsUser,
}) => {
  // Deliberately the REAL user, never the simulated one: the switch that turns the
  // client view on has to stay reachable to turn it off.
  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;

  // Les options ET la sélection courante passent par ici : masquer les unes sans
  // les autres laisse le vrai nom dans la puce déjà posée.
  const maskNames = (options) => (
    obfuscate && options
      ? options.map((option) => ({ ...option, label: obfuscateUsername(option.label) }))
      : options
  );

  // Name what is out of scope with real numbers: "229 lignes" alone left the admin
  // guessing whether the gap came from deleted rows or from placeholders.
  const breakdown = (() => {
    if (!outOfScope?.total) return 'Aucune visite hors périmètre sur la période.';
    const parts = [];
    if (outOfScope.deleted) parts.push(`${outOfScope.deleted} supprimée${outOfScope.deleted > 1 ? 's' : ''}`);
    if (outOfScope.special) parts.push(`${outOfScope.special} hors magasin`);
    if (outOfScope.didNotHappen) parts.push(`${outOfScope.didNotHappen} non effectuée${outOfScope.didNotHappen > 1 ? 's' : ''}`);
    return `${outOfScope.total} visite${outOfScope.total > 1 ? 's' : ''} hors périmètre sur la période (${parts.join(', ')}).`;
  })();
  const { startDate, endDate, setStartDate, setEndDate } = useVisits();
  const [isExpanded, setIsExpanded] = useState(true);

  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Résumé des filtres appliqués (vue mobile repliée)
  const getFilterSummary = () => {
    const parts = [];
    if (startDate && endDate) {
      parts.push(`${startDate.toLocaleDateString('fr-FR')} - ${endDate.toLocaleDateString('fr-FR')}`);
    }
    if (selectedSalespeople && selectedSalespeople.length > 0) {
      parts.push(selectedSalespeople.length === 1
        ? selectedSalespeople[0].label
        : `${selectedSalespeople.length} personnes`);
    }
    if (selectedShopSigns && selectedShopSigns.length > 0) {
      parts.push(`${selectedShopSigns.length} enseigne${selectedShopSigns.length > 1 ? 's' : ''}`);
    }
    parts.push(comparisonLabel(comparisonMode, 'short'));
    if (showOutOfScope) parts.push('hors périmètre inclus');
    return parts.join(' • ');
  };

  return (
    // Sticky from `md` only: stacked on one column the expanded bar is taller
    // than a landscape phone viewport, and pinning it at top-0 would put its
    // last filters permanently out of reach. The page background is repeated
    // because a transparent sticky bar lets content slide visibly under it.
    <div className="relative md:sticky md:top-0 z-30 -mx-4 px-4 pt-4 pb-3 bg-gray-50 dark:bg-[#0d0d0d] border-b border-gray-200/70 dark:border-gray-800/70">
      {/* Toggle header pour mobile/tablet - masqué en desktop */}
      <div
        className="flex items-center justify-between cursor-pointer md:hidden relative"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded && (
          <div className="absolute inset-x-0 text-center">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtres</div>
          </div>
        )}

        <div className={`flex-1 ${isExpanded ? 'invisible' : 'visible'} max-w-[calc(100%-2rem)]`}>
          {!isExpanded && (
            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 text-left">
              {getFilterSummary()}
            </div>
          )}
        </div>

        <div className="ml-3 z-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'transform rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Contenu du filtre - toujours visible en desktop */}
      <div className={`${isExpanded ? 'block' : 'hidden'} md:block md:pt-0 pt-4`}>
        {isAdmin && (
          <div className="flex items-center justify-between mb-2">
            <span className="hidden md:block text-xs font-medium text-gray-500 dark:text-gray-400">
              Filtres
            </span>
            <PopoverMenu
              label="Options d'affichage"
              title="Options d'affichage"
              badge={getAdminBadge()}
              active={!!showOutOfScope}
              className="ml-auto"
            >
              <label className="flex items-start gap-2 cursor-pointer text-xs text-gray-700 dark:text-gray-200 mb-3">
                <input
                  type="checkbox"
                  checked={!!viewAsUser}
                  onChange={(e) => setViewAsUser?.(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span>
                  Voir en tant que client
                  {/* Naming the out-of-scope checkbox here made two controls homonyms. */}
                  <span className="block text-gray-500 dark:text-gray-400">
                    Onglets admin et noms masqués. Vos droits et les données interrogées sont inchangés.
                  </span>
                </span>
              </label>

              <label className={`flex items-start gap-2 text-xs text-gray-700 dark:text-gray-200 ${
                viewAsUser ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
              }`}>
                <input
                  type="checkbox"
                  checked={!!showOutOfScope && !viewAsUser}
                  disabled={!!viewAsUser}
                  onChange={(e) => setShowOutOfScope(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span>
                  Afficher les visites hors périmètre
                  <span className="block text-gray-500 dark:text-gray-400">
                    Détails, Durées et Calendrier
                  </span>
                </span>
              </label>

              <p className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                {breakdown}
                {' '}
                {showOutOfScope
                  ? 'Elles pèsent sur les moyennes de durée.'
                  : 'Elles sont masquées.'}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                Le Résumé, Points de vente et Effectif les excluent toujours : les y
                réintégrer inventerait des magasins et de l'effectif.
              </p>
            </PopoverMenu>
          </div>
        )}

        {/* One row on a wide screen. The univers selector is conditional, so the
            column count follows it — a fixed 4 left the fifth filter alone on a
            second line, which read as a separate group rather than a wrapped one.
            Tailwind needs whole class names, hence the two literals. */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${
          universOptions.length > 0 ? 'lg:grid-cols-3 xl:grid-cols-5' : 'lg:grid-cols-4'
        }`}>
          {/* Sélection de dates */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Période
            </label>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onDatesChange={handleDateRangeChange}
              maxDateRange={{ value: 12, unit: 'months' }}
              relative={false}
              includeFuture={true}
            />
          </div>

          {/* Univers — affiché seulement quand le projet en a plusieurs. Un projet
              mono-univers n'a rien à filtrer : lui montrer une liste à une entrée
              suggérerait un choix qui n'existe pas. */}
          {universOptions.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
                Univers ({universOptions.length - 1})
              </label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                name="univers"
                options={universOptions}
                value={universOptions.find((o) => o.value === selectedUnivers) || universOptions[0]}
                onChange={(opt) => setSelectedUnivers(opt?.value || UNIVERS_ALL)}
                isSearchable={false}
                isDisabled={loading || !selectedProject}
                menuPortalTarget={document.body}
                // Our order, not the alphabet: "Tous univers" regroups the others and
                // belongs first. Select sorts by label unless told otherwise, which
                // buried it in the middle of the list.
                disableSort
              />
            </div>
          )}

          {/* Sélection de vendeur */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Effectif {selectedProject && salespeople && `(${salespeople.length})`}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={maskNames(salespeople)}
              isMulti
              // Masquée elle aussi : une sélection faite en admin garderait sinon
              // le vrai nom dans sa puce après bascule en vue client.
              value={maskNames(selectedSalespeople)}
              onChange={(opts) => setSelectedSalespeople(opts || [])}
              isLoading={loading}
              placeholder="Tout l'effectif"
              isDisabled={loading || !selectedProject}
              menuPortalTarget={document.body}
              isClearable={true}
              enableSelectAll
            />
          </div>

          {/* Période de comparaison */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
              Comparer à
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={Object.values(COMPARISON_MODES).map((mode) => ({
                value: mode,
                label: COMPARISON_LABELS[mode].option,
              }))}
              value={{ value: comparisonMode, label: comparisonLabel(comparisonMode, 'option') }}
              onChange={(opt) => setComparisonMode(opt?.value || COMPARISON_MODES.PREVIOUS_YEAR)}
              isSearchable={false}
              isDisabled={loading || !selectedProject}
              menuPortalTarget={document.body}
            />
          </div>

          {/* Sélection des enseignes */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Enseignes {selectedProject && shopSigns && `(${shopSigns.length})`}
            </label>
            <Select
              isMulti
              name="shopSigns"
              options={shopSigns || []}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Filtrer par enseigne..."
              noOptionsMessage={() => "Aucune enseigne trouvée"}
              value={selectedShopSigns}
              onChange={setSelectedShopSigns}
              isClearable={true}
              menuPortalTarget={document.body}
              isLoading={loading && selectedProject}
              isDisabled={!selectedProject}
              enableSelectAll
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default VisitsSelectionHeader;
